import { useEffect, useRef } from 'react'

/**
 * A number field wearing its unit, under the caption naming it: empty it hints the unit as a
 * placeholder, and once a number is typed the unit rides right behind the digits — 50 reads as
 * 50 kg, spaced the way the app writes a weight everywhere else. The field stays a real number
 * input, stepper and all, per the web design spec; the mask is drawn over it, after an
 * invisible mirror of the typed text that gives it its place.
 */
export function UnitInput({
  value,
  unit,
  label,
  focused = false,
  size = 'text-lg',
  step = '0.1',
  onValue,
  onSettled,
}: {
  value: string
  unit: string
  /** The caption over the field, which is also what the field is called. */
  label: string
  /** Whether the field takes the focus as it arrives — the one a panel opens for. */
  focused?: boolean
  /** The type scale of the field, which the mirror has to match to the pixel. */
  size?: string
  /**
   * What the stepper moves by, and what the browser will hold the value to. A field showing a
   * figure worked out from another — a rate written four ways — takes `any`, since a number
   * off a division is under no obligation to land on a tenth.
   */
  step?: string
  onValue: (next: string) => void
  /** Called as the field is left, where what was typed is kept rather than sent on every key. */
  onSettled?: (() => void) | undefined
}) {
  const field = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (focused) {
      field.current?.focus()
    }
  }, [focused])

  return (
    <label className="block">
      <span className="mb-1 block text-sm text-ink-muted">{label}</span>
      <span className="relative block">
        <input
          ref={field}
          type="number"
          inputMode="decimal"
          min="0"
          step={step}
          aria-label={label}
          placeholder={unit}
          value={value}
          onChange={(event) => {
            onValue(event.target.value)
          }}
          onBlur={onSettled}
          className={`w-full rounded-2xl bg-surface p-3 ${size} outline-none ring-1 ring-white/10 placeholder:text-ink-muted focus:ring-2 focus:ring-accent`}
        />
        {value !== '' && (
          <span
            aria-hidden
            className={`pointer-events-none absolute inset-y-0 left-3 flex items-center ${size}`}
          >
            <span className="invisible whitespace-pre">{value}</span>
            <span className="text-ink-muted">&nbsp;{unit}</span>
          </span>
        )}
      </span>
    </label>
  )
}
